import { useTranslation } from "react-i18next";
import mglLogo from "../../../images/mglLogo.png";
// The B2B block is MGL Logistic & Spedition, a different brand from the
// parcel service — it carries its own logo.
import mglGroupLogo from "../../../images/mglLogistics.jpeg";

/**
 * Two side-by-side brand blocks:
 *   - MGL Parcel — B2C (retail) copy for the international shopper.
 *   - MGL Group  — B2B (corporate) copy for the logistics partner.
 *
 * Replaced the older "Retail Direction / Corporate Direction" banner-image
 * layout (2026-08). Copy comes from the i18n bundle (`mgl_parcel_*` /
 * `mgl_group_*` keys) so the site keeps ge/en parity.
 */
const BannerSection = () => {
  const { t } = useTranslation();

  return (
    <div className="px-12 md:px-6 w-full flex flex-col items-center justify-center">
      <div className="w-full max-w-[80%] md:max-w-[100%] grid grid-cols-2 md:grid-cols-1 gap-6">
        {/* -------- MGL Parcel (B2C) -------- */}
        <div className="rounded-3xl bg-white border border-gray-200 shadow-sm p-6 flex flex-col">
          <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
            <img
              src={mglLogo}
              alt="MGL Parcel"
              className="h-12 md:h-10 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-xs text-teal_600 font-bold">
                {t("mgl_parcel_tag") || "B2C მომსახურება"}
              </span>
              <h6 className="text-lg md:text-base font-bold text-black_900">
                {t("mgl_parcel_headline") ||
                  "MGL Parcel — შენი საერთაშორისო შოპინგის მარტივი გზა."}
              </h6>
            </div>
          </div>

          <p className="text-sm mt-4 leading-relaxed sm:text-xs">
            {t("mgl_parcel_body_intro")}
          </p>
          <p className="text-sm mt-2 leading-relaxed sm:text-xs">
            {t("mgl_parcel_body_we_handle")}
          </p>

          {/* warehouse chip row */}
          <p className="mt-4 text-xs font-bold text-gray-500">
            {t("our_international_warehouses") ||
              "საერთაშორისო საწყობები იქ, სადაც შენ ყიდულობ"}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold">
              🇨🇳 {t("china")} · {t("warehouse_modes_china")}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold">
              🇺🇸 {t("usa")} · {t("warehouse_modes_usa")}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold">
              🇬🇷 {t("greece")} · {t("warehouse_modes_greece")}
            </span>
          </div>

          {/* predictable schedule */}
          <p className="mt-5 text-sm font-bold text-black_900">
            {t("mgl_parcel_schedule_heading") ||
              "წინასწარ განსაზღვრული რეისები. პროგნოზირებადი მიწოდება."}
          </p>
          <p className="text-sm mt-1 leading-relaxed sm:text-xs">
            {t("mgl_parcel_schedule_body")}
          </p>

          {/* 10kg free delivery */}
          <p className="text-sm mt-3 leading-relaxed sm:text-xs">
            {t("mgl_parcel_10kg_free")}
          </p>
          <p className="text-sm mt-1 leading-relaxed sm:text-xs">
            {t("mgl_parcel_10kg_over")}
          </p>

          {/* punchline */}
          <p className="mt-4 text-base font-bold text-teal_600">
            {t("mgl_parcel_punchline") ||
              "ერთი სერვისი. სამი ქვეყანა. მთელი საქართველო."}
          </p>
          <p className="text-sm mt-1 leading-relaxed sm:text-xs">
            {t("mgl_parcel_punchline_body")}
          </p>
          <p className="text-sm mt-2 leading-relaxed sm:text-xs font-semibold">
            {t("mgl_parcel_cta") ||
              "შეუკვეთე მსოფლიოს ნებისმიერ ონლაინ მაღაზიაში. მიიღე საქართველოში!"}
          </p>
        </div>

        {/* -------- MGL Group (B2B) -------- */}
        <div className="rounded-3xl bg-teal_600 text-white shadow-sm p-6 flex flex-col">
          <div className="flex items-center gap-3 pb-3 border-b border-white/20">
            <img
              src={mglGroupLogo}
              alt="MGL Logistic & Spedition"
              className="h-12 md:h-10 object-contain bg-white rounded-lg px-2 py-1.5"
            />
            <div className="flex flex-col">
              <span className="text-xs opacity-80 font-bold">
                {t("mgl_group_tag") || "B2B ლოგისტიკური სერვისი"}
              </span>
              <h6 className="text-lg md:text-base font-bold">
                {t("mgl_group_headline") ||
                  "MGL Group — ბულგარულ-ქართული საერთაშორისო ლოგისტიკური პარტნიორი ბიზნესისთვის."}
              </h6>
            </div>
          </div>

          <p className="text-sm mt-4 leading-relaxed sm:text-xs">
            {t("mgl_group_body_p1")}
          </p>
          <p className="text-sm mt-2 leading-relaxed sm:text-xs">
            {t("mgl_group_body_p2")}
          </p>
          <p className="text-sm mt-2 leading-relaxed sm:text-xs">
            {t("mgl_group_body_p3")}
          </p>

          <p className="mt-4 text-sm font-bold">
            {t("mgl_group_network_heading") ||
              "ლოგისტიკური ქსელი: აზია — ევროპა — კავკასია — ცენტრალური აზია"}
          </p>
          <p className="text-sm mt-1 leading-relaxed sm:text-xs">
            {t("mgl_group_network_body")}
          </p>

          <p className="mt-4 text-sm font-bold">
            {t("mgl_group_reach_heading") ||
              "ბიზნესები, რომლებსაც ვემსახურებით:"}
          </p>
          <p className="text-sm mt-1 leading-relaxed sm:text-xs">
            {t("mgl_group_reach_body") ||
              "საქართველო, სომხეთი, აზერბაიჯანი, ყაზახეთი, უზბეკეთი, ცენტრალური აზია, ევროპა."}
          </p>

          <p className="mt-4 text-sm font-bold">
            {t("mgl_group_poti_heading") || "ფოთის პორტი"}
          </p>
          <p className="text-sm mt-1 leading-relaxed sm:text-xs">
            {t("mgl_group_poti_body")}
          </p>

          <p className="mt-4 text-sm font-bold">
            {t("mgl_group_modes_heading") ||
              "საზღვაო · სახმელეთო · საჰაერო · სარკინიგზო · მულტიმოდალური"}
          </p>
          <p className="text-sm mt-1 leading-relaxed sm:text-xs">
            {t("mgl_group_modes_body")}
          </p>

          <p className="mt-4 text-base font-bold">
            {t("mgl_group_punchline") ||
              "MGL Group — ერთი პარტნიორი მთელი ლოგისტიკური ჯაჭვისთვის."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BannerSection;
